import { isObject } from 'lodash/fp';
import type {
    SnowflakeColumnFqn,
    SnowflakeFileFormatFqn,
    SnowflakeSchemaFqn,
    SnowflakeStageFqn,
    SnowflakeStoredProcedureFqn,
    SnowflakeTableFqn
} from 'app/lib/types/server';
import { isNil } from 'app/lib/fp/isNil';
import { identifier } from './sql_generator.js';

type PossibleFqn =
    | SnowflakeTableFqn
    | SnowflakeSchemaFqn
    | SnowflakeStageFqn
    | SnowflakeFileFormatFqn
    | SnowflakeColumnFqn
    | SnowflakeStoredProcedureFqn
    | null;

export function isSnowflakeSchemaFqn(fqn?: PossibleFqn): fqn is SnowflakeSchemaFqn {
    return !isNil(fqn) && 'database' in fqn && 'schema' in fqn;
}

export function isSnowflakeStageFqn(fqn?: PossibleFqn): fqn is SnowflakeStageFqn {
    return isSnowflakeSchemaFqn(fqn) && 'stage' in fqn;
}

export function isSnowflakeFileFormatFqn(fqn?: PossibleFqn): fqn is SnowflakeFileFormatFqn {
    return isSnowflakeSchemaFqn(fqn) && 'fileFormat' in fqn;
}

export function isSnowflakeStoredProcedureFqn(
    fqn?: PossibleFqn
): fqn is SnowflakeStoredProcedureFqn {
    return isSnowflakeSchemaFqn(fqn) && 'storedProcedure' in fqn;
}

export function isSnowflakeTableFqn(fqn?: PossibleFqn): fqn is SnowflakeTableFqn {
    return isSnowflakeSchemaFqn(fqn) && 'table' in fqn;
}

export function isSnowflakeColumnFqn(fqn?: PossibleFqn): fqn is SnowflakeColumnFqn {
    return isSnowflakeTableFqn(fqn) && 'column' in fqn;
}

export function fqnEquals(a?: PossibleFqn, b?: PossibleFqn): boolean {
    // mimicing an xor here
    if (isSnowflakeColumnFqn(a) || isSnowflakeColumnFqn(b)) {
        if (isSnowflakeColumnFqn(a) && isSnowflakeColumnFqn(b)) {
            return (
                a.database === b.database &&
                a.schema === b.schema &&
                a.table === b.table &&
                a.column === b.column
            );
        }
        return false;
    }

    if (isSnowflakeTableFqn(a) || isSnowflakeTableFqn(b)) {
        if (isSnowflakeTableFqn(a) && isSnowflakeTableFqn(b)) {
            return a.database === b.database && a.schema === b.schema && a.table === b.table;
        }
        return false;
    }

    if (isSnowflakeStageFqn(a) || isSnowflakeStageFqn(b)) {
        if (isSnowflakeStageFqn(a) && isSnowflakeStageFqn(b)) {
            return a.database === b.database && a.schema === b.schema && a.stage === b.stage;
        }
        return false;
    }

    if (isSnowflakeFileFormatFqn(a) || isSnowflakeFileFormatFqn(b)) {
        if (isSnowflakeFileFormatFqn(a) && isSnowflakeFileFormatFqn(b)) {
            return (
                a.database === b.database && a.schema === b.schema && a.fileFormat === b.fileFormat
            );
        }
        return false;
    }

    if (isSnowflakeSchemaFqn(a) && isSnowflakeSchemaFqn(b)) {
        return a.database === b.database && a.schema === b.schema;
    }

    return false;
}

export const toStringFqn = (
    fqn:
        | string
        | SnowflakeTableFqn
        | SnowflakeSchemaFqn
        | SnowflakeColumnFqn
        | SnowflakeStageFqn
        | SnowflakeFileFormatFqn
        | SnowflakeStoredProcedureFqn
) => {
    if (!isObject(fqn)) {
        return fqn;
    }

    if (isSnowflakeColumnFqn(fqn)) {
        return identifier(fqn.database, fqn.schema, fqn.table, fqn.column);
    }

    if (isSnowflakeTableFqn(fqn)) {
        return identifier(fqn.database, fqn.schema, fqn.table);
    }

    if (isSnowflakeStageFqn(fqn)) {
        return identifier(fqn.database, fqn.schema, fqn.stage);
    }

    if (isSnowflakeFileFormatFqn(fqn)) {
        return identifier(fqn.database, fqn.schema, fqn.fileFormat);
    }

    if (isSnowflakeStoredProcedureFqn(fqn)) {
        return identifier(fqn.database, fqn.schema, fqn.storedProcedure);
    }

    return identifier(fqn.database, fqn.schema);
};
